import { ComponentProps } from 'preact';
import { Label } from '../../UNSAFE_Label';
import { TextField } from '../../UNSAFE_TextField';
import { InlineUserAssistance, UserAssistanceDensityType } from '../../UNSAFE_UserAssistance';
import { useMessageSeverity } from '../PRIVATE_useMessageSeverity';
import { merge } from '../../utils/UNSAFE_stringUtils';
import { useFocusWithin } from '../UNSAFE_useFocusWithin';
import { useFormFieldContextProps } from '../UNSAFE_useFormFieldContextProps';
import { useId } from '../UNSAFE_useId';
import { AriaAttributesSignalExcluded } from '../../utils/UNSAFE_attributeUtils';

type InlineUserAssistanceProps = ComponentProps<typeof InlineUserAssistance>;
type PickedUserAssistanceProps = Pick<InlineUserAssistanceProps, 'messages'>;

type UseTextFieldProps<V> = PickedUserAssistanceProps & {
  ariaDescribedBy?: string;
  contentVariant?: 'input' | 'textarea';
  helpSourceLink?: string;
  helpSourceText?: string;
  isDisabled?: boolean;
  isFocused?: boolean;
  isLoading?: boolean;
  isReadonly?: boolean;
  isRequiredShown?: boolean;
  labelEdge?: ComponentProps<typeof TextField>['labelEdge'] | 'none';
  styleVariant?: 'default' | 'embedded';
  userAssistanceDensity?: UserAssistanceDensityType;
  value?: V;
};

/**
 * A custom hook to determine the props for a component that renders
 * a text field
 */
export function useTextField<V>({
  ariaDescribedBy,
  contentVariant,
  helpSourceLink,
  helpSourceText,
  isDisabled,
  isFocused,
  isLoading,
  isReadonly,
  isRequiredShown,
  labelEdge,
  messages,
  styleVariant,
  userAssistanceDensity,
  value
}: UseTextFieldProps<V>) {
  const baseId = useId();
  const labelId = labelEdge !== 'none' ? `${baseId}-label` : undefined;
  const inputId = !isReadonly ? `${baseId}-input` : undefined;
  const uaId = !(isDisabled || isReadonly) ? `${baseId}-ua` : undefined;
  const helpIconId = userAssistanceDensity === 'compact' ? `${baseId}-helpIcon` : undefined;

  const { focusProps, isFocused: isInputFocused } = useFocusWithin({ isDisabled });
  const hasValue = value !== undefined && (typeof value !== 'string' || value !== '');

  // Create the form field context
  const formFieldContext = useFormFieldContextProps({
    hasValue,
    isDisabled,
    isFocused,
    isInputFocused,
    isLoading,
    isReadonly
  });

  // Determine the highest severity from the messages
  const messageSeverity = useMessageSeverity(messages);

  // Determine the label variant
  const labelVariant: ComponentProps<typeof Label>['variant'] =
    labelEdge !== 'none' ? labelEdge : undefined;

  return {
    baseId,
    formFieldContext,
    inputProps: {
      id: inputId,
      'aria-describedby': merge([helpIconId, uaId, ariaDescribedBy]),
      'aria-invalid':
        messageSeverity === 'error'
          ? 'true'
          : (undefined as AriaAttributesSignalExcluded['aria-invalid']),
      variant: styleVariant,
      onBlur: focusProps?.onfocusout,
      onFocus: focusProps?.onfocusin
    },
    labelProps: {
      forId: inputId,
      helpIconId,
      helpSourceLink,
      helpSourceText,
      id: labelId,
      isRequiredShown,
      userAssistanceDensity,
      variant: labelVariant
    },
    textFieldProps: {
      contentVariant,
      statusVariant: /error|warning/.test(messageSeverity ?? '')
        ? (messageSeverity as 'error' | 'warning')
        : undefined,
      styleVariant
    },
    userAssistanceProps: {
      id: uaId
    }
  };
}
